import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { withStyles } from '@mui/styles';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';

// Contexts
import { withTenant, withTranslate } from '../../../core/TenantProvider';
// Components, Atoms, Molecules, Organisms
import APButton from '../../atoms/APButton';
import APHeading from '../../atoms/APHeading';
import APLoadingSpinner from '../../atoms/APLoadingSpinner';
import APConfirmationModal from '../APConfirmationModal';
// Redux
import { ReducerState } from '../../../redux/modules';
import { action as BankDetailsActions } from '../../../redux/modules/bankDetails';
// Files in the same folder
import APBankDetailsStyle from './styles/APBankDetails.style';
import { ReactComponent as BankIcon } from './assets/icon-finance.svg';
// Types
import { AffiliateDetailsAttributes } from '../../../core/types/AffiliateDetails';
import { MASK_CLASS } from '../../../core/utils/Constants/Constants';

interface APBankDetailsProps {
    classes: any;
    translate: (key: string) => string;
    getBankDetailsV2: (affiliateNumber: string) => void;
    bankDetailsV2: object[];
    bankDetailsV2LoadingState: string;
    affiliateDetails: AffiliateDetailsAttributes | null;
    deleteBankDetails: Function;
    addBankDetailsSectionOpen: MouseEventHandler<HTMLButtonElement>;
    isSuperUser: boolean;
}

type BankDetailsObjectType = {
    id: number,
    affiliateAccountId: null,
    affiliateNumber: string,
    country: string,
    currency: string,
    countryId: number,
    currencyId: number,
    accountName: string,
    accountNumber: string,
    affiliateBankId: number,
    affiliateBank: string,
    branchAddress: string,
    branchName: string,
    swiftCode: string,
    localCodeType: string,
    localCode: string,
    postcode: null,
    viaBank: false,
    viaAffiliateBankId: null,
    viaAffiliateBank: string,
    viaBranchName: string,
    viaBranchAddress: string,
    viaSwiftCode: string,
    viaCountry: null,
    viaCountryId: null,
    viaLocalCode: string,
    viaLocalCodeType: string,
    iban: null,
    charges: string,
    settlement: string,
    beneficiaryAddress: string,
    beneficiaryCity: string,
    beneficiaryPostalCode: string,
    beneficiaryEmail: string,
    branchPhoneNumber: string,
    ffcDetail: string,
    accountType: string,
    fundsOutMethod: string,
    accountStatus: string,
    createdDate: string,
    createdBy: string,
    updatedDate: string,
    updatedBy: string
}

const APBankDetails: React.FC<any> = ({
    classes,
    translate,
    getBankDetailsV2,
    bankDetailsV2,
    bankDetailsV2LoadingState,
    affiliateDetails,
    deleteBankDetails,
    addBankDetailsSectionOpen,
    isSuperUser
}: APBankDetailsProps) => {

    const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

    useEffect(() => {
        if (affiliateDetails?.affiliate?.affiliate_Number) {
            getBankDetailsV2(affiliateDetails.affiliate.affiliate_Number);
        }
    }, [affiliateDetails, getBankDetailsV2]);

    const activeBankDetailsSearch = (bankDetailsV2) => {
        return bankDetailsV2?.find((details) => details.accountStatus === 'ACTIVE') ?? null;
    };

    const bankDetails: BankDetailsObjectType | null = activeBankDetailsSearch(bankDetailsV2);

    const handleDeleteBank = () => {
        setIsConfirmationOpen(true);
    }

    const handleCancelBankDeletion = () => {
        setIsConfirmationOpen(false);
    }

    const handleConfirmBankDeletion = () => {
        if (bankDetails) {
            deleteBankDetails({
                ...bankDetails,
                accountStatus: 'INACTIVE',
                affiliateAccountId: affiliateDetails?.affiliateProfileId
            });

            setIsConfirmationOpen(false);
        }
    }

    const isBankDetailsLoading: boolean = bankDetailsV2LoadingState === 'LOADING';

    const bankDetailsFields = useMemo(() => [
        { label: translate('settings.bankDetails.details.accountName'), value: bankDetails?.accountName },
        { label: translate('settings.bankDetails.details.paymentCountry'), value: bankDetails?.country },
        { label: translate('settings.bankDetails.details.paymentCurrency'), value: bankDetails?.currency },
        { label: translate('settings.bankDetails.details.accountNo'), value: bankDetails?.accountNumber },
        { label: translate('settings.bankDetails.details.sortCode'), value: bankDetails?.localCode },
        { label: translate('settings.bankDetails.details.bankName'), value: bankDetails?.affiliateBank },
        { label: translate('settings.bankDetails.details.bankHeadOfficeAddress'), value: bankDetails?.branchAddress },
        { label: translate('settings.bankDetails.details.swiftbic'), value: bankDetails?.swiftCode }
    ], [bankDetails]);

    const renderBankDetails = () => (<>
        {bankDetailsFields.map(({ label, value }) => (
            <div key={label} className={classes.detailsItem}>
                <div className={classes.detailsItemHeading}>{label}</div>
                <div className={classnames(classes.detailsItemValue, MASK_CLASS)}>{value || '-'}</div>
            </div>
        ))}

        {!isSuperUser && (
            <APButton
                className={classes.deleteBankButton}
                onClick={handleDeleteBank}
                data-testid='delete-bank-button'
                variant="text"
                color="primary"
            >
                {translate('settings.bankDetails.editBankDetails.delete')}
            </APButton>
        )}
    </>);

    const renderNoBankFound = () => (
        <div className={classes.noBankFoundWrapper}>
            <div className={classes.bankIconRing}>
                <BankIcon className={classes.bankIcon} />
            </div>
            <div className={classes.noBankRightSection}>
                <div className={classes.noBankDetailsText}>
                    {translate('settings.bankDetails.noBankFound.heading')}
                </div>
                {!isSuperUser && (
                    <APButton
                        className={classes.addBankButton}
                        onClick={addBankDetailsSectionOpen}
                        data-testid='add-bank-button'
                        variant="text"
                        color="primary">
                        {translate('settings.bankDetails.noBankFound.addBank')}
                    </APButton>
                )}
            </div>
        </div>
    )

    return (
        <>
            <APConfirmationModal
                open={isConfirmationOpen}
                handleConfirm={handleConfirmBankDeletion}
                handleCancel={handleCancelBankDeletion}
                type="deleteBankDetails"
            />

            <div className={classes.sectionHeader}>
                <APHeading
                    className={classes.sectionHeading}
                    variant="body1">
                    {translate('settings.bankDetails.heading')}
                </APHeading>
            </div>

            <div className={classes.sectionContent}>
                {isBankDetailsLoading ? <APLoadingSpinner /> : bankDetails ? renderBankDetails() : renderNoBankFound()}
            </div >

            {bankDetails && !isSuperUser && (
                <APButton
                    className={classes.addBankButton}
                    onClick={addBankDetailsSectionOpen}
                    data-testid='add-bank-button'
                    variant="text"
                    color="primary">
                    {translate('settings.bankDetails.editBankDetails.addBank')}
                </APButton>
            )}
        </>
    )
}

const mapStateToProps = (store: ReducerState) => ({
    affiliateDetails: store.user.affiliateDetails,
    bankDetailsV2: store.bankDetails.bankDetailsV2,
    bankDetailsV2LoadingState: store.bankDetails.bankDetailsV2LoadingState,
    deleteBankDetailsLoadingState: store.bankDetails.deleteBankDetailsLoadingState
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getBankDetailsV2: BankDetailsActions.getBankDetailsV2,
    deleteBankDetails: BankDetailsActions.deleteBankDetails
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withTenant(
        withTranslate(
            withStyles(APBankDetailsStyle)(
                APBankDetails
            )
        )
    )
);